import { toast } from "@/shared/hooks/useToast";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createTmsToken,
  deleteTmsTokens,
  fetchTmsAccounts,
  fetchTmsChannels,
  fetchTmsSchema,
} from "../api/tmsToken";
import type {
  CreateTmsTokenDto,
  TmsAccountRow,
  TmsAccountsParams,
  TmsSchemaResponse,
} from "../types/tokens-types";

export function useTmsTokenManagement() {
  const [channels, setChannels] = useState<string[]>([]);
  const [currentChannel, setCurrentChannel] = useState<string>("");
  const [schema, setSchema] = useState<TmsSchemaResponse>({ fields: [] });

  const [searchValue, setSearchValue] = useState("");

  const [accounts, setAccounts] = useState<TmsAccountRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // 마지막 사용한 페이지네이션 파라미터 저장 (refresh 시 재사용)
  const lastParams = useRef<TmsAccountsParams>({
    page: 1,
    page_size: 50,
  });

  const fetchAccountsWithParams = useCallback(async (params: TmsAccountsParams) => {
    lastParams.current = params;
    setIsLoading(true);
    try {
      const result = await fetchTmsAccounts(params);
      setAccounts(result.accounts);
      setTotalCount(result.total_count);
    } catch (error) {
      toast({
        title: "계정 로드 실패",
        description: error instanceof Error ? error.message : "계정을 불러오는 데 실패했습니다.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 채널 + 스키마 초기 로드, 새로고침 시에도 계정 재조회
  const load = useCallback(async () => {
    try {
      const channelsData = await fetchTmsChannels();
      setChannels(channelsData);

      if (channelsData.length > 0 && !currentChannel) {
        const firstChannel = channelsData[0];
        setCurrentChannel(firstChannel);

        try {
          const schemaData = await fetchTmsSchema(firstChannel);
          setSchema(schemaData);
        } catch (schemaError) {
          console.error("Failed to load initial schema:", schemaError);
        }
      }
    } catch (error) {
      toast({
        title: "데이터 로드 실패",
        description: error instanceof Error ? error.message : "데이터를 불러오는 데 실패했습니다.",
        variant: "destructive",
      });
    }

    // 현재 파라미터로 계정 재조회
    await fetchAccountsWithParams(lastParams.current);
  }, [currentChannel, fetchAccountsWithParams]);

  // 컴포넌트 마운트 시 채널/스키마 로드
  useEffect(() => {
    const loadInitial = async () => {
      try {
        const channelsData = await fetchTmsChannels();
        setChannels(channelsData);

        if (channelsData.length > 0) {
          const firstChannel = channelsData[0];
          setCurrentChannel(firstChannel);

          try {
            const schemaData = await fetchTmsSchema(firstChannel);
            setSchema(schemaData);
          } catch (schemaError) {
            console.error("Failed to load initial schema:", schemaError);
          }
        }
      } catch (error) {
        toast({
          title: "채널 로드 실패",
          description: error instanceof Error ? error.message : "채널을 불러오는 데 실패했습니다.",
          variant: "destructive",
        });
      }
    };
    void loadInitial();
  }, []);

  const fetchSchema = async (channel: string) => {
    try {
      const schemaData = await fetchTmsSchema(channel);
      setCurrentChannel(channel);
      setSchema(schemaData);
    } catch (error) {
      toast({
        title: "스키마 로드 실패",
        description: error instanceof Error ? error.message : "스키마를 불러오는 데 실패했습니다.",
        variant: "destructive",
      });
    }
  };

  const createToken = async (dto: CreateTmsTokenDto) => {
    try {
      await createTmsToken(dto);
      toast({
        title: "토큰 생성 완료",
        description: "TMS 토큰이 성공적으로 생성되었습니다.",
      });
      // 현재 파라미터로 다시 조회
      await fetchAccountsWithParams(lastParams.current);
    } catch (error) {
      toast({
        title: "토큰 생성 실패",
        description: error instanceof Error ? error.message : "토큰 생성에 실패했습니다.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteAccounts = async (rows: TmsAccountRow[]) => {
    try {
      const accountsToDelete = rows.map((r) => ({
        channel: r.channel,
        appId: r.appId,
        userId: r.userId,
      }));

      await deleteTmsTokens(accountsToDelete);

      toast({
        title: "토큰 삭제 완료",
        description: `${rows.length}개의 토큰이 삭제되었습니다.`,
      });

      // 현재 파라미터로 다시 조회
      await fetchAccountsWithParams(lastParams.current);
    } catch (error) {
      toast({
        title: "토큰 삭제 실패",
        description: error instanceof Error ? error.message : "토큰 삭제에 실패했습니다.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return {
    channels,
    currentChannel,
    schema,

    accounts,
    totalCount,
    isLoading,

    load,
    fetchAccountsWithParams,
    searchValue,
    setSearchValue,

    fetchSchema,
    createToken,
    deleteAccounts,
  };
}
